import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShieldCheck, Plus, AlertTriangle } from 'lucide-react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { parseISO, format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { usePagination } from '@/hooks/usePagination';
import { permissionAdminService, profileService, type PermissionRow } from '@/services';
import { getAllPermissions } from '@prolarus/shared';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import NewPermissionDialog from '@/components/security/NewPermissionDialog';
import SyncPermissionsDialog from '@/components/security/SyncPermissionsDialog';
import { toast } from 'sonner';

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

export default function SecurityPermissions() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'security-permissions' });

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [allPermissions, setAllPermissions] = useState<PermissionRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<PermissionRow | null>(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  // Compute permissions defined in code but missing from the database
  const missingPermissions = useMemo(() => {
    const dbCodes = new Set(allPermissions.map(p => p.code));
    return getAllPermissions().filter(p => !dbCodes.has(p.code));
  }, [allPermissions]);

  // Fetch company ID on mount
  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!user) return;
      const { data: profile } = await profileService.get(user.id);
      if (profile?.company_id) {
        setCompanyId(profile.company_id);
      } else {
        setIsLoading(false);
      }
    };
    fetchCompanyId();
  }, [user]);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    const result = await permissionAdminService.list(companyId);
    if (result.error) {
      toast.error('Failed to load permissions');
    } else {
      setAllPermissions(result.data);
    }
    setIsLoading(false);
  }, [companyId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Client-side search filtering
  const filteredPermissions = useMemo(() => {
    if (!debouncedSearch) return allPermissions;
    const search = debouncedSearch.toLowerCase();
    return allPermissions.filter(
      (p) =>
        p.name?.toLowerCase().includes(search) ||
        p.code?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search)
    );
  }, [allPermissions, debouncedSearch]);

  // Client-side pagination
  const paginatedPermissions = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredPermissions.slice(start, start + pageSize);
  }, [filteredPermissions, pageIndex, pageSize]);

  const totalCount = filteredPermissions.length;

  // Delete handlers
  const handleDeleteClick = (permission: PermissionRow) => {
    setPermissionToDelete(permission);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!permissionToDelete || !companyId || !user?.id) return;

    try {
      const result = await permissionAdminService.delete(
        permissionToDelete.id,
        permissionToDelete as unknown as Record<string, unknown>,
        { companyId, userId: user.id }
      );

      if (result.error) throw result.error;

      toast.success('Permission deleted successfully');
      await fetchPermissions();
    } catch (error: any) {
      console.error('Error deleting permission:', error);
      toast.error(error.message || 'Failed to delete permission');
    } finally {
      setDeleteDialogOpen(false);
      setPermissionToDelete(null);
    }
  };

  // Column definitions
  const columnDefs = useMemo<ColDef<PermissionRow>[]>(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: 'System',
        field: 'is_system',
        width: 100,
        cellRenderer: (params: ICellRendererParams<PermissionRow>) => {
          if (!params.data) return null;
          return params.data.is_system ? (
            <Badge variant="secondary" className="gap-1">
              <ShieldCheck className="h-3 w-3" />
              System
            </Badge>
          ) : null;
        },
      },
      {
        headerName: 'Active',
        field: 'is_active',
        width: 100,
        cellRenderer: (params: ICellRendererParams<PermissionRow>) => {
          if (!params.data) return null;
          return (
            <span className={params.data.is_active ? 'text-green-600' : 'text-muted-foreground'}>
              {params.data.is_active ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        headerName: 'Created',
        field: 'created',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<PermissionRow>) =>
          formatDateTime(params.value),
      },
      {
        headerName: 'Modified',
        field: 'modified',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<PermissionRow>) =>
          formatDateTime(params.value),
      },
      {
        colId: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: ICellRendererParams<PermissionRow>) => {
          if (!params.data || params.data.is_system) return null;
          return (
            <div className="flex items-center justify-center h-full">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(params.data!);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="animate-slide-up space-y-4">
      <div>
        <h1 className="page-title">Permissions</h1>
        <p className="text-sm text-muted-foreground">Manage user and group permissions.</p>
      </div>

      {missingPermissions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
          <CardContent className="flex items-center gap-3 py-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-sm flex-1">
              <strong>{missingPermissions.length} permission{missingPermissions.length > 1 ? 's' : ''}</strong> defined in code {missingPermissions.length > 1 ? 'are' : 'is'} not in your database.
            </span>
            <Button variant="outline" size="sm" onClick={() => setSyncDialogOpen(true)}>
              Add Missing Permissions
            </Button>
          </CardContent>
        </Card>
      )}

      <AGDataGrid<PermissionRow>
        rowData={paginatedPermissions}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search by name, code, or category..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRowClicked={(data: PermissionRow) => {
          navigate(`/security/permissions/${data.id}`);
        }}
        toolbarActions={
          <Button variant="outline" size="sm" onClick={() => setNewDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Permission
          </Button>
        }
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Permission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the permission "{permissionToDelete?.name}"? This will remove it from all users and groups. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {companyId && user && (
        <>
          <NewPermissionDialog
            open={newDialogOpen}
            onOpenChange={setNewDialogOpen}
            context={{ companyId, userId: user.id }}
            onCreated={fetchPermissions}
          />
          <SyncPermissionsDialog
            open={syncDialogOpen}
            onOpenChange={setSyncDialogOpen}
            missingPermissions={missingPermissions}
            context={{ companyId, userId: user.id }}
            onSynced={fetchPermissions}
          />
        </>
      )}
    </div>
  );
}
